import { FunctionComponent, ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { classNames } from "@/utils/classNames";

import { useGetAnalyticsMutation } from "../../../redux/analytics/analytics.api";
import { IFilterWithValues } from "../../List/components/FilterModal";

import { ActiveFilterChips } from "../components/ActiveFilterChips";
import { CampaignStepsModal } from "../components/CampaignStepsModal";
import { DeepDiveActions, DeepDiveCampaignRef } from "../components/DeepDiveActions";
import { AnalyticsFilterBar } from "../components/AnalyticsFilterBar";
import { AnalyticsSelection } from "../hooks/useAnalyticsSelection";
import { KpiBreakdownBucket, SegmentDimension } from "../schema/analytics";
import { buildContactsNavigation } from "../utils/buildContactsNavigation";
import { buildLeadsNavigation, campaignNodesOf } from "../utils/buildLeadsNavigation";
import { KpiKey } from "../utils/kpiFilterMap";
import { buildSegmentFilter } from "../utils/segmentFilterMap";

import { DeepDiveReport } from "./DeepDiveReport";

/** The dimensions the breakdown section offers; always requested so the tabs are populated. */
const SEGMENT_BY: ("role" | "geo" | "industry")[] = ["role", "geo", "industry"];

export interface DeepDiveColumnProps {
	selection: AnalyticsSelection;
	/** Side-by-side mode: the controls move into a card and the report stacks. */
	isComparing?: boolean;
	/** Column heading shown above the controls while comparing. */
	header?: ReactNode;
	/** Shown instead of the report while the column has nothing selected yet. */
	placeholder?: ReactNode;
}

/**
 * One selection and the report it produces. The comparison view renders two of
 * these; each fetches independently, so a slow column never blocks the other.
 */
export const DeepDiveColumn: FunctionComponent<DeepDiveColumnProps> = ({
	selection,
	isComparing = false,
	header,
	placeholder,
}) => {
	const navigate = useNavigate();
	const [getAnalytics, { data, isLoading }] = useGetAnalyticsMutation();

	const { payload, selectedLists, dateFromToValue, activeFilters } = selection;
	const hasSelection = selectedLists.length > 0;

	useEffect(() => {
		getAnalytics({ ...payload, segmentBy: SEGMENT_BY }).unwrap();
	}, [payload]);

	const buildContactsTarget = useCallback(
		(kpiKey: KpiKey, extraFilter?: IFilterWithValues | null) =>
			buildContactsNavigation({
				kpiKey,
				selectedLists,
				dateRange: {
					from: dateFromToValue?.startDate ? new Date(dateFromToValue.startDate) : null,
					to: dateFromToValue?.endDate ? new Date(dateFromToValue.endDate) : null,
				},
				additionalFilters: activeFilters,
				segmentFilter: extraFilter ?? null,
			}),
		[selectedLists, dateFromToValue, activeFilters]
	);

	const handleKpiClick = useCallback(
		(kpiKey: KpiKey) => {
			const target = buildContactsTarget(kpiKey);

			navigate({ pathname: target.pathname, search: target.search });
		},
		[navigate, buildContactsTarget]
	);

	const handleBucketOpenInContacts = useCallback(
		(dimension: SegmentDimension, bucket: KpiBreakdownBucket, kpiKey: KpiKey) => {
			const segmentFilter = buildSegmentFilter(dimension, bucket.key, bucket.label);
			const target = buildContactsTarget(kpiKey, segmentFilter);

			navigate({ pathname: target.pathname, search: target.search });
		},
		[navigate, buildContactsTarget]
	);

	const [openCampaign, setOpenCampaign] = useState<DeepDiveCampaignRef | null>(null);

	/**
	 * "View leads" for one list, or for the whole selection. It reuses the same routing
	 * rule as a clicked KPI: one list opens that list's own leads view, several open
	 * Contacts, which is the only view that can span lists.
	 *
	 * Opened in a new tab, unlike the KPI drill-throughs: this one is a side trip off a
	 * report you are still reading, and navigating away would cost the whole selection.
	 */
	const handleOpenLeads = useCallback(
		(listId: number | null) => {
			const target = buildLeadsNavigation(
				listId === null ? selectedLists : selectedLists.filter((list) => list.id === listId)
			);

			window.open(
				`${target.pathname}${target.search ? `?${target.search}` : ""}`,
				"_blank",
				"noopener,noreferrer"
			);
		},
		[selectedLists]
	);

	const handleBucketDrillDown = useCallback(
		(dimension: SegmentDimension, bucket: KpiBreakdownBucket) => {
			const segmentFilter = buildSegmentFilter(dimension, bucket.key, bucket.label);

			if (segmentFilter) {
				selection.addFilter(segmentFilter);
			}
		},
		[selection.addFilter]
	);

	return (
		<div className={classNames("flex flex-col w-full min-w-0", isComparing ? "" : "items-start")}>
			{header ? <div className="mb-2 w-full">{header}</div> : null}

			<AnalyticsFilterBar
				campaigns={selection.campaigns}
				dateFromToValue={selection.dateFromToValue}
				isStacked={isComparing}
				listOptions={selection.listOptions}
				selectedLists={selection.selectedLists}
				senderUserIds={selection.senderUserIds}
				onCampaignToggle={selection.onCampaignToggle}
				onDateChange={selection.onDateChange}
				onListContentFilterToggle={selection.onListContentFilterToggle}
				onListSelect={selection.onListSelect}
				onSenderChange={selection.onSenderChange}
			/>

			{openCampaign ? (
				<CampaignStepsModal
					campaignName={openCampaign.campaignName}
					initialNodes={campaignNodesOf(selection.listData, openCampaign)}
					isOpened
					listId={openCampaign.listId}
					listName={openCampaign.listName}
					onClose={() => setOpenCampaign(null)}
				/>
			) : null}

			<ActiveFilterChips
				filters={selection.activeFilters}
				onClearAll={selection.clearFilters}
				onRemove={selection.removeFilter}
			/>

			{placeholder && !hasSelection ? (
				<div className="w-full mt-4">{placeholder}</div>
			) : (
				<DeepDiveReport
					actions={
						<DeepDiveActions
							selectedLists={selection.selectedLists}
							onOpenCampaign={setOpenCampaign}
							onOpenLeads={handleOpenLeads}
						/>
					}
					data={data}
					isCompact={isComparing}
					isLoading={isLoading}
					onBucketDrillDown={handleBucketDrillDown}
					onBucketOpenInContacts={handleBucketOpenInContacts}
					onKpiClick={handleKpiClick}
				/>
			)}
		</div>
	);
};
